/**
 * <AgentBadge /> — names the responsible agent (M3X.1 v2 F.7).
 *
 * Per commandment 65 + AGENT_SPECS.md §8, every AI-attributed
 * surface in the UI names the responsible agent (one of Tashghil /
 * Rawatib / Mutaba'aa / Khidmat). Latin in code, Arabic in display.
 *
 * Color per AGENT_SPECS §8 + the brand palette:
 *   - Tashghil (تشغيل)  — Brand Teal
 *   - Rawatib (رواتب)   — Brand Blue
 *   - Mutaba'aa (متابعة) — Brand Purple
 *   - Khidmat (خدمات)   — Brand Deep
 */

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

export type AgentName = "tashghil" | "rawatib" | "mutabaa" | "khidmat";

export interface AgentBadgeProps {
  agent: AgentName;
  /** Optional decorative icon to render before the label. */
  icon?: ReactNode;
  /** Compact (icon + short label) or full (icon + Arabic + English). */
  variant?: "compact" | "full";
  className?: string;
}

const AGENT_TONES: Record<AgentName, string> = {
  tashghil:
    "bg-brand-teal/10 text-brand-teal border border-brand-teal/30",
  rawatib: "bg-brand-blue/10 text-brand-blue border border-brand-blue/30",
  mutabaa:
    "bg-brand-purple/10 text-brand-purple border border-brand-purple/30",
  khidmat: "bg-brand-deep/10 text-brand-deep border border-brand-deep/30",
};

export function AgentBadge({
  agent,
  icon,
  variant = "compact",
  className = "",
}: AgentBadgeProps): ReactNode {
  const { t } = useTranslation();
  const arabic = t(`agent.${agent}.arabic`);
  const english = t(`agent.${agent}.english`);

  const tone = AGENT_TONES[agent];
  const baseClasses =
    "inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium";

  return (
    <span
      className={`${baseClasses} ${tone} ${className}`}
      data-agent={agent}
      data-testid={`agent-badge-${agent}`}
      title={`${english} — ${arabic}`}
    >
      {icon}
      {variant === "compact" ? (
        <span>{english}</span>
      ) : (
        <>
          <span dir="rtl">{arabic}</span>
          <span className="text-text-tertiary">·</span>
          <span>{english}</span>
        </>
      )}
    </span>
  );
}
